import {CapAlertService} from "./cap-alert.service";
import {TranslateService} from "@ngx-translate/core";
import {Location} from "@angular/common";
import {Injectable, Injector, NgZone} from "@angular/core";
import {Router} from "@angular/router";
import {AppPermissionService} from "../../services/app-permission.service";
import {log} from "../../../log/log";
import {CapExceptionMapper} from "./cap-exception-mapper";
import {RouteHistoryService} from "../../services/route-history.service";
import {CapAlertLevel} from "./cap-alert-level.enum";


export abstract class ExceptionHandlerRule {

  errorHandled(error: any) {
    if (this.isRuleApplicable(error)) {
      return this.handleError(error);
    }
    return false;
  }

  abstract isRuleApplicable(error: any): boolean;

  /**
   * @return true if error is handled and no further handling necessary, else false
   */
  abstract handleError(error: any): boolean;
}

@Injectable()
export class ResolverValidationExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected translate: TranslateService) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    return error.status === 400 && error.error && error.error.results;
  }

  handleError(error: any) {
    const message = [];
    for (const result of error.error.results) {
      if (result.path && result.path.split(".").length > 2) {
        message.push(this.translate.instant(result.path.split(".").pop()) + ": " + this.translate.instant(result.messageKey));
      } else {
        message.push(this.translate.instant(result.messageKey));
      }
      const params = {fehlerMeldung: message.join("<br />")};
      this.capAlertService.warning(this.translate.instant("ValidierungsFehler", params));
    }
    return true;
  }
}

@Injectable()
export class SearchResultValidationExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected translate: TranslateService,
              protected location: Location,
              protected routeHistoryService: RouteHistoryService,
              protected router: Router) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    return error.status === 400 && error.error && error.error.results;
  }

  handleError(error: any) {

    if (this.routeHistoryService.previousRoute && this.routeHistoryService.previousRoute !== '/') {
      for (const result of error.error.results) {
        const params = {fehlerMeldung: this.translate.instant(result.messageKey)};
        this.capAlertService.warning(this.translate.instant("ValidierungsFehler", params), CapAlertLevel.CONTENT_HEADER, true);
      }
      this.location.back();
    } else {
      this.router.navigate(['/home/invalidrequest']);
    }
    return true;
  }
}

@Injectable()
export class UnauthorizedExceptionHandler extends ExceptionHandlerRule {

  constructor(protected zone: NgZone,
              protected injector: Injector) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    return error.status === 401 || error.status === 405;
  }

  handleError(error: any) {
    this.zone.run(() => {
      setTimeout(() => this.injector.get(Router).navigate(['./unauthorized']));
    });
    return true;
  }
}

@Injectable()
export class MizenExceptionHandler extends ExceptionHandlerRule {

  constructor(protected zone: NgZone,
              protected injector: Injector,
              protected capAlertService: CapAlertService,
              protected translate: TranslateService) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    const environment = (window as any).environment;
    const mizenUrl = environment.mizenBasePath?.toLowerCase();
    const errorUrl = error.url?.toLowerCase();
    return mizenUrl && errorUrl && errorUrl.includes(mizenUrl) && error.status === 401;
  }

  handleError(error: any) {
    this.zone.run(() => {
      this.capAlertService.danger(this.translate.instant("MizenNichtErreichbar"));
    });
    return true;
  }
}

@Injectable()
export class NoPermissionExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected translate: TranslateService,
              protected zone: NgZone,
              protected injector: Injector) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    return error.status === 403;
  }

  handleError(error: any) {
    this.zone.run(() => {
      this.capAlertService.info(this.translate.instant("KeineBerechtigungFürDieseAktion"));
      this.injector.get(AppPermissionService).refreshPermissions();
    });
    return true;
  }
}

@Injectable()
export class TooManyRequestsExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected zone: NgZone,
              protected injector: Injector,
              protected translate: TranslateService) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    return error.status === 429;
  }

  handleError(error: any) {
    if (error.error?.exceptionName === "TooManyRequestsException") {
      this.zone.run(() => {
        setTimeout(() => this.injector.get(Router).navigate(['security/toomanyicpgrundstueckrequests']));
      });
    } else {
      this.zone.run(() => {
        setTimeout(() => this.injector.get(Router).navigate(['security/toomanyrequests']));
      });
    }
    return true;
  }
}

@Injectable()
export class FremdsystemErrorExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected translate: TranslateService) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    return error.status === 502 && error.error && error.error.exceptionName
      && error.error.exceptionName === "FremdsystemException";
  }

  handleError(error: any) {
    this.capAlertService.warning(this.translate.instant("FremdsystemFehler"));
    return true;
  }
}


@Injectable()
export class FremdsystemNeskoErrorExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected translate: TranslateService) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    const errorUrl = error.url?.toLowerCase();
    return errorUrl && errorUrl.includes('nesko');
  }

  handleError(error: any) {
    this.capAlertService.warning(this.translate.instant("FremdsystemNeskoFehler"));
    return true;
  }
}

@Injectable()
export class ElasticSearchExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected translate: TranslateService) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    return error.status === 500 && error.error && error.error.exceptionName
      && error.error.exceptionName === "EsException";
  }

  handleError(error: any) {
    this.capAlertService.warning(this.translate.instant("ErrorWhileLoading"));
    return true;
  }
}

@Injectable()
export class IcpPrintExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected translate: TranslateService) {
    super();
  }

  handleError(error: any): boolean {
    this.capAlertService.warning(this.translate.instant("IcpPrintException"));
    return true;
  }

  isRuleApplicable(error: any): boolean {
    return error.status === 500 && error.message.indexOf("print/icp/grundstueck") > 0;
  }

}

@Injectable()
export class LogAndAlertExceptionHandler extends ExceptionHandlerRule {

  constructor(protected capAlertService: CapAlertService,
              protected translate: TranslateService,
              protected zone: NgZone,
              protected exceptionMapper: CapExceptionMapper) {
    super();
  }

  isRuleApplicable(error: any): boolean {
    return true; // for all
  }

  handleError(error: any) {
    log.error(error, false);
    if (error.rejection) {
      error = error.rejection;
    }
    this.alertMessage(error);
    return false;
  }

  protected alertMessage(error) {
    if (error.status === 0) {
      this.alertWarningMessage(error);
    }
    this.alertErrorMessage(error);
  }

  private alertErrorMessage(error) {
    this.zone.run(() => {
      this.capAlertService.danger(this.exceptionMapper.getMessageFrom(error), CapAlertLevel.GLOBAL, false, this.exceptionMapper.getStackFrom(error));
    });
  }

  private alertWarningMessage(error) {
    this.zone.run(() => {
      this.capAlertService.warning(this.exceptionMapper.getMessageFrom(error));
    });
  }
}

